<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import OTPModal from './OTPModal.vue';
import { useOTPHandler } from '../hooks/useOTPHandler';
import type { FormField, Section } from '../types/formTypeStructure';
import { findLinkedEmailField, findSectionIndexForField } from '../types/formUtils';
import { showToast } from '../utils/toastHelpers';

interface Props {
  field: FormField;
  formValues: Record<string, any>;
  currentLang?: string;
  onClearForm: () => void;
  allSections?: Section[];
  onSubmit?: () => void;
  onNext?: () => void;
  isLastSection?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  currentLang: 'fr',
  allSections: () => [],
  isLastSection: false,
});

const isModalOpen = ref(false);

// Surveiller la valeur de l'OTP pour la validation
const otpValue = computed(() => props.formValues[props.field.fieldName]);

// Trouver le champ Email lié (dans une section précédente)
const otpSectionIndex = computed(() => 
  findSectionIndexForField(props.field.fieldName, props.allSections)
);

const linkedEmailField = computed(() => {
  if (otpSectionIndex.value === -1) return undefined;
  return findLinkedEmailField(props.field, props.allSections, otpSectionIndex.value);
});

const linkedEmail = computed(() => 
  linkedEmailField.value 
    ? props.formValues[linkedEmailField.value.fieldName]
    : null
);

// Valeur du champ lié (peut être un email ou un OSC ID)
const linkedFieldValue = computed(() => 
  linkedEmailField.value 
    ? props.formValues[linkedEmailField.value.fieldName]
    : null
);

// Flag pour savoir si on a déjà tenté d'envoyer l'OTP au montage
const hasTriedToSendOTP = ref(false);

const {
  sendOTP,
  validateOTP,
  resendOTP,
  resetOTPSentState,
  handleCallbackRedirect,
  isSending,
  isResending,
  resendCooldown,
  otpSent,
  otpValidated,
  serverResponse,
  error,
} = useOTPHandler(props.field.otpFormFieldExecOptions, props.field.fieldName);

// Ouvrir le modal automatiquement au montage (l'OTP a normalement déjà été envoyé dans handleNext)
onMounted(() => {
  console.log('🔍 [OTPRenderer] Composant monté, ouverture du modal...', {
    fieldName: props.field.fieldName,
    isModalOpen: isModalOpen.value,
    linkedField: linkedEmailField.value?.fieldName,
    linkedValue: linkedFieldValue.value,
    otpAlreadySent: otpSent.value
  });
  
  // Ouvrir immédiatement le modal
  isModalOpen.value = true;
  
  // Fallback : Envoyer l'OTP seulement si :
  // 1. L'OTP n'a pas déjà été envoyé (via handleNext)
  // 2. Le champ lié a une valeur
  // 3. On n'a pas déjà tenté d'envoyer
  // Cela permet de gérer le cas où l'utilisateur navigue directement vers la section OTP
  if (linkedFieldValue.value && linkedEmailField.value && !otpSent.value) {
    const hasSendConfig = props.field.otpFormFieldExecOptions?.sendOTPApiConfig || 
                          props.field.otpFormFieldExecOptions?.linkedEmailFieldName;
    if (hasSendConfig && !hasTriedToSendOTP.value) {
      hasTriedToSendOTP.value = true;
      setTimeout(async () => {
        try {
          console.log('🔄 [OTPRenderer] Fallback: Envoi automatique de l\'OTP au montage (OTP non envoyé dans handleNext) pour:', linkedFieldValue.value);
          await sendOTP(props.formValues);
          console.log('✅ [OTPRenderer] OTP envoyé avec succès (fallback)');
        } catch (err) {
          console.error("❌ [OTPRenderer] Erreur lors de l'envoi automatique de l'OTP au montage (fallback):", err);
          hasTriedToSendOTP.value = false; // Réessayer si erreur
        }
      }, 300);
    } else if (!linkedFieldValue.value) {
      console.warn('⚠️ [OTPRenderer] Le champ lié n\'a pas de valeur, l\'OTP ne sera pas envoyé automatiquement');
    }
  } else if (otpSent.value) {
    console.log('✅ [OTPRenderer] OTP déjà envoyé dans handleNext, pas besoin de renvoyer');
  }
  
  // Double vérification après un court délai
  setTimeout(() => {
    if (!isModalOpen.value) {
      console.log('⚠️ [OTPRenderer] Le modal n\'est pas ouvert, réouverture...');
      isModalOpen.value = true;
    } else {
      console.log('✅ [OTPRenderer] Modal OTP ouvert');
    }
  }, 100);
});

const closeModal = () => {
  isModalOpen.value = false;
};

const handleSendOTP = async () => {
  try {
    await sendOTP(props.formValues);
  } catch (err) {
    console.error("Erreur lors de l'envoi de l'OTP:", err);
  }
};

const handleValidateOTP = async () => {
  try {
    // Récupérer la valeur OTP (peut être une string ou un objet)
    let otpCode = "";
    if (typeof otpValue.value === "string") {
      otpCode = otpValue.value;
    } else if (otpValue.value && typeof otpValue.value === "object" && (otpValue.value as any).otp) {
      otpCode = (otpValue.value as any).otp;
    } else {
      // Essayer de récupérer directement depuis formValues
      otpCode = props.formValues[props.field.fieldName] || "";
      if (typeof otpCode !== "string") {
        otpCode = "";
      }
    }
    
    const otpLength = props.field.otpFormFieldExecOptions?.otpLength ?? 6;
    if (!otpCode || otpCode.length !== otpLength) {
      const errorMessage = `Code OTP incomplet. Veuillez saisir ${otpLength} chiffres.`;
      console.error("Code OTP incomplet. Longueur attendue:", otpLength, "Reçue:", otpCode.length);
      showToast(errorMessage, 'error');
      return;
    }
    
    await validateOTP(otpCode, props.formValues);
    // Succès : le toast sera géré par le composant si nécessaire
  } catch (err: any) {
    console.error("Erreur lors de la validation de l'OTP:", err);
    // Afficher un toast d'erreur pour informer l'utilisateur
    const errorMessage = err?.message || "Le code OTP saisi est incorrect. Veuillez réessayer.";
    showToast(`❌ ${errorMessage}`, 'error');
  }
};

const handleCallbackRedirectWrapper = (url: string) => {
  handleCallbackRedirect(url, props.onClearForm);
};

// Auto-validation si activée
const autoValidate = computed(() => props.field.otpFormFieldExecOptions?.autoValidate ?? false);

const handleOTPComplete = async (otpCode: string) => {
  if (autoValidate.value && !otpValidated.value && !isSending.value) {
    try {
      await validateOTP(otpCode, props.formValues);
      // Succès : le toast sera géré par le composant si nécessaire
    } catch (err: any) {
      console.error("Erreur lors de l'auto-validation de l'OTP:", err);
      // Afficher un toast d'erreur pour informer l'utilisateur
      const errorMessage = err?.message || "Le code OTP saisi est incorrect. Veuillez réessayer.";
      showToast(`❌ ${errorMessage}`, 'error');
    }
  }
};

// Fonction pour renvoyer le code
const handleResendOTP = async () => {
  try {
    // Réinitialiser l'état avant de renvoyer
    resetOTPSentState();
    await resendOTP(props.formValues);
    // Note: On pourrait utiliser un toast ici si disponible
    console.log("Code OTP renvoyé avec succès !");
  } catch (err) {
    console.error("Erreur lors du renvoi de l'OTP:", err);
  }
};
</script>

<template>
  <div>
    <!-- Debug: afficher l'état du modal -->
    <div v-if="false" class="text-xs text-gray-500 p-2 bg-yellow-100">
      Debug OTPRenderer: isModalOpen={{ isModalOpen }}, field={{ field?.fieldName }}
    </div>
    
    <OTPModal
      :is-open="isModalOpen"
      :field="field"
      :current-lang="currentLang"
      :form-values="formValues"
      :server-response="serverResponse"
      :is-sending="isSending"
      :is-resending="isResending"
      :resend-cooldown="resendCooldown"
      :otp-validated="otpValidated"
      :linked-email="linkedEmail"
      :is-last-section="isLastSection"
      @close="closeModal"
      @sendOTP="handleSendOTP"
      @validateOTP="handleValidateOTP"
      @resendOTP="handleResendOTP"
      @callbackRedirect="handleCallbackRedirectWrapper"
      @submit="onSubmit"
      @next="onNext"
      @otpComplete="autoValidate ? handleOTPComplete : undefined"
    />
  </div>
</template>

